/**
 * Parcel name resolution — single source of truth.
 *
 * Why this module exists
 * ----------------------
 * Parcel names historically lived in three places, written by different
 * ingestion paths and read by different views:
 *
 *   1. GeoJSON feature properties (from Earth Engine vector assets) —
 *      populated upstream of the app; field names vary by data source
 *      (KML `Name`, ISTAT cadastral `NOMECP`/`NOMEPT`, custom shapefiles
 *      `farm_field_name`, Marcus's pipeline `parcel_name`, …).
 *
 *   2. Firestore `farms/{farmId}/parcels/{docId}` — populated by the
 *      Python KMZ ingestion function (`name`), `ConfigUploadModal`
 *      (`displayName` + `name` + `farm_field_name`), and admin edits via
 *      `ParcelListTab` (`customName`). Marcus's analytics sync historically
 *      wrote no name field at all, which is why WLD's sidebar fell back to
 *      docIds like "WLD1".
 *
 *   3. Analytics payload `parcel.name` — comes from `get-farm-summary`;
 *      for Marcus-pushed properties this is often the parcel_id, not a
 *      human-readable name.
 *
 * Going forward every ingestion path writes a canonical `displayName` field
 * to the Firestore parcel doc, and every reader calls `resolveParcelName`.
 * Demo aliasing (DemoModeContext) sits on top of this — it receives the
 * resolved name as a fallback and is unaffected.
 *
 * See PHASE_NEXT_to_richard_integration_response_v17 (consolidation plan).
 */

export const trimDisplayName = (s) => (s == null ? '' : String(s).trim());

/**
 * Strip a leading parcel ID from a display string. IDs are for lookup only;
 * the UI shows the name. Preserves internal spaces (e.g. "Hints Meadow"
 * stays as is). Returns the display unchanged when there's no id prefix.
 */
export const nameOnlyFromDisplay = (displayValue, idValue) => {
  if (!displayValue || !idValue) return trimDisplayName(displayValue) || displayValue;
  const s = trimDisplayName(displayValue);
  const id = String(idValue).trim();
  if (!id || s === id) return s;
  if (s.startsWith(id)) {
    const nameOnly = trimDisplayName(s.slice(id.length));
    return nameOnly || s;
  }
  return s;
};

/**
 * Derive a display name from a GeoJSON parcel feature's properties.
 *
 * Used by ingestion paths (e.g. `syncParcelAnalyticsToFirestore`) to compute
 * the canonical `displayName` once at write time, and as a last-resort
 * fallback for callers that have a feature but no Firestore doc.
 *
 * Field priority below reflects the source-of-truth ordering across the
 * upstream data we receive — keep in sync with the Python KMZ ingestion
 * and any future ingestion paths.
 */
export const getParcelNameFromFeature = (feature) => {
  const p = feature?.properties || {};
  const idForStrip = p.parcel_id || p.analyticsParcelName || '';
  const customOrDisplay = p.customName || p.displayName || p.farm_field_name
    || p.parcel_name || p.parcel_nam
    || p.NOMECP || p.NOMEPT || p.label || p.Label;
  if (customOrDisplay) return nameOnlyFromDisplay(customOrDisplay, idForStrip) || customOrDisplay;
  const nameOrId = p.Name || p.name;
  const isGenericParcel = /^Parcel\s+\d+$/i.test(String(nameOrId || ''));
  if (nameOrId && !isGenericParcel) return nameOnlyFromDisplay(nameOrId, idForStrip) || nameOrId;
  return p.analyticsParcelName || p.parcel_id || (nameOrId || '');
};

/**
 * Canonical parcel-name resolver. **All app reads should use this** instead
 * of inline `customName || displayName || name || ...` chains.
 *
 * Precedence:
 *   1. parcelDoc.customName       — admin override (set via ParcelListTab)
 *   2. parcelDoc.displayName      — canonical name from ingestion
 *   3. parcelDoc.farm_field_name  — legacy field from config uploads
 *   4. parcelDoc.name             — legacy field from Python KMZ ingestion
 *   5. fallbackFeature properties — last-resort GeoJSON fallback
 *   6. parcelDoc.id               — final fallback (technical docId)
 *
 * Any candidate equal to the parcel's id is treated as "no name" and we fall
 * through — this matches the WLD sidebar bug where `name === parcel_id ===
 * "WLD1"` should not surface as the display label.
 *
 * @param {object|null} parcelDoc        Firestore parcel doc data (or null)
 * @param {object|null} fallbackFeature  Optional GeoJSON feature (with .properties)
 * @returns {string} resolved display name, or the docId, or '' if nothing
 */
export const resolveParcelName = (parcelDoc, fallbackFeature = null) => {
  const doc = parcelDoc || {};
  const idForStrip = doc.parcel_id || doc.analyticsParcelName || doc.id || '';
  const trimmedId = String(idForStrip).trim();
  const candidates = [doc.customName, doc.displayName, doc.farm_field_name, doc.name];
  for (const candidate of candidates) {
    const trimmed = trimDisplayName(candidate);
    if (!trimmed) continue;
    // A candidate that's identical to the parcel's id (the WLD-style case)
    // isn't a real name — fall through to the next candidate.
    if (trimmedId && trimmed === trimmedId) continue;
    return nameOnlyFromDisplay(trimmed, trimmedId) || trimmed;
  }
  if (fallbackFeature) {
    const fromFeature = getParcelNameFromFeature(fallbackFeature);
    const trimmedFromFeature = trimDisplayName(fromFeature);
    if (trimmedFromFeature && trimmedFromFeature !== trimmedId) return trimmedFromFeature;
  }
  return doc.id || trimmedId || '';
};
